/**
 * Taking a payment, from the button to the run starting — the add-in port of
 * `asthra-v4-web/src/new-document/use-checkout.ts`.
 *
 * ## What is different here
 *
 * - **No TanStack, no AuthProvider.** The token comes from `getSessionToken()`
 *   (baked into `request`), and the post-payment refresh/navigation is done by
 *   the `onStarted` callback the shell supplies — not query invalidation.
 * - **The system browser, not a tab.** `openExternal` hands the Dodo URL to the
 *   user's default browser via `Office.context.ui.openBrowserWindow`. There is
 *   no popup-block signal (so no `blocked` phase) and no reliable
 *   `visibilitychange` "returned" event (the checkout is a separate window), so
 *   polling leans on the timer plus a manual **checkNow()**.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  createCheckout,
  refreshIntent,
  startPaidDraft,
  type CheckoutHandoff,
  type CheckoutItem,
  type CheckoutRun,
  type PaymentIntent,
  type StartedPaidDraft,
} from "../../api/client";
import { openExternal } from "../services/openExternal";
import { isOpenable, pollDelay, readIntent } from "./checkout-machine";

export type CheckoutPhase =
  /** Nothing started. The dialog is showing an order. */
  | { readonly kind: "idle" }
  /** Asking our server to open a checkout. */
  | { readonly kind: "opening" }
  /** The provider's page is open in the browser and we are waiting. */
  | { readonly kind: "waiting"; readonly handoff: CheckoutHandoff }
  /**
   * There is a checkout, but it is not one a browser can open — the fake
   * provider hands back `fake://checkout/<ref>`. Kept as a phase because nothing
   * is wrong: it keeps polling so a developer who posts the event by hand sees
   * the dialog pick it up.
   */
  | { readonly kind: "simulated"; readonly handoff: CheckoutHandoff }
  /** Paid; the draft is being started. */
  | { readonly kind: "starting"; readonly handoff: CheckoutHandoff }
  /**
   * Paid, and the draft did not start. Its own phase, carrying the ref, so the
   * retry stays pointed at the payment that already happened.
   */
  | { readonly kind: "owed"; readonly ref: string; readonly error: unknown; readonly retrying: boolean }
  /** A checkout is already open for an overlapping but DIFFERENT selection. */
  | { readonly kind: "conflict"; readonly ref: string; readonly url: string; readonly refs: readonly string[] }
  /** Terminal, and not a fault worth a notice: the buyer or the provider said no. */
  | { readonly kind: "refused"; readonly reason: "failed" | "expired"; readonly detail: string | null }
  /** Something threw. Classified by the caller. */
  | { readonly kind: "error"; readonly error: unknown };

export interface UseCheckout {
  readonly phase: CheckoutPhase;
  /**
   * Open a checkout for this cart and start watching it.
   *
   * ``run`` is the draft the payment buys; pass ``null`` for a purchase that
   * starts no run through this pipeline — the flat custom-submission unlock,
   * whose run is dispatched separately by the caller. A run-less checkout
   * settles by calling ``onUnlocked`` rather than ``startPaidDraft``.
   */
  readonly begin: (items: readonly CheckoutItem[], run: CheckoutRun | null) => Promise<void>;
  /** Try again to start a draft that was paid for and did not begin. Never charges. */
  readonly retryDraft: () => Promise<void>;
  /** Poll the provider right now (the "I've paid — check now" button). */
  readonly checkNow: () => void;
  /** Give up watching. Does not cancel the payment — nothing here can. */
  readonly reset: () => void;
}

export function useCheckout(options: {
  readonly onStarted: (started: StartedPaidDraft) => void;
  /**
   * A run-less checkout cleared. Only fired when ``begin`` was called with a
   * ``null`` run — the custom unlock — so the caller can proceed with its own
   * dispatch (``startCustomFlow``). ``ref`` is the completed intent.
   */
  readonly onUnlocked?: (ref: string) => void | Promise<void>;
}): UseCheckout {
  const [phase, setPhase] = useState<CheckoutPhase>({ kind: "idle" });

  const onStartedRef = useRef(options.onStarted);
  onStartedRef.current = options.onStarted;
  const onUnlockedRef = useRef(options.onUnlocked);
  onUnlockedRef.current = options.onUnlocked;
  // Whether the checkout in flight carries a run. A run-less one settles by
  // reporting the unlock instead of claiming a draft to start.
  const hasRunRef = useRef(true);

  // Written synchronously so a poll and a manual check landing together cannot
  // both reach `startPaidDraft`. The route is idempotent, so a double call is
  // survivable — this just stops two of them racing to navigate.
  const settlingRef = useRef(false);
  // Bumped by checkNow() to fire an immediate poll and reset the backoff.
  const [checkTick, setCheckTick] = useState(0);

  const reset = useCallback(() => {
    settlingRef.current = false;
    setPhase({ kind: "idle" });
  }, []);

  const begin = useCallback(async (items: readonly CheckoutItem[], run: CheckoutRun | null) => {
    hasRunRef.current = run !== null;
    setPhase({ kind: "opening" });
    let handoff: CheckoutHandoff;
    try {
      handoff = await createCheckout({ items: [...items], run: run ?? undefined });
    } catch (error) {
      const conflict = pendingCheckout(error);
      if (conflict !== null) {
        setPhase({ kind: "conflict", ...conflict });
        return;
      }
      setPhase({ kind: "error", error });
      return;
    }
    setPhase(phaseFor(handoff));
  }, []);

  const settle = useCallback(async (ref: string) => {
    if (settlingRef.current) return;
    settlingRef.current = true;
    setPhase((current) =>
      current.kind === "waiting" || current.kind === "simulated"
        ? { kind: "starting", handoff: current.handoff }
        : current.kind === "owed"
          ? { ...current, retrying: true }
          : current,
    );
    // A run-less checkout (the custom unlock) has no draft to claim — the money
    // cleared, and the caller dispatches its own run. Report and stop.
    if (!hasRunRef.current) {
      try {
        await onUnlockedRef.current?.(ref);
      } catch (error) {
        settlingRef.current = false;
        setPhase({ kind: "owed", ref, error, retrying: false });
      }
      return;
    }
    try {
      const started = await startPaidDraft(ref);
      onStartedRef.current(started);
    } catch (error) {
      // Paid, and the draft did not start. Held as `owed` so a retry points at
      // the payment that cleared rather than opening a new order.
      settlingRef.current = false;
      setPhase({ kind: "owed", ref, error, retrying: false });
    }
  }, []);

  const retryDraft = useCallback(async () => {
    setPhase((current) => {
      if (current.kind === "owed") void settle(current.ref);
      return current;
    });
  }, [settle]);

  const checkNow = useCallback(() => {
    setCheckTick((n) => n + 1);
  }, []);

  // The watch. Runs while a checkout is open, stops the moment it is not.
  const watching = phase.kind === "waiting" || phase.kind === "simulated";
  const ref = watching ? phase.handoff.ref : null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: checkTick re-triggers the poll loop by design
  useEffect(() => {
    if (ref === null) return;
    let live = true;

    const look = async () => {
      if (!live) return;
      let intent: PaymentIntent;
      try {
        intent = await refreshIntent(ref);
      } catch {
        // A poll that cannot reach the server is not an answer. Try again next tick.
        return;
      }
      if (!live) return;
      const outcome = readIntent(intent, Date.now());
      if (outcome.kind === "settle") {
        await settle(ref);
        return;
      }
      if (outcome.kind === "refused") {
        setPhase({ kind: "refused", reason: outcome.reason, detail: outcome.detail });
      }
    };

    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      timer = setTimeout(() => void tick(), pollDelay(attempt));
      attempt += 1;
    };
    const tick = async () => {
      await look();
      if (live) schedule();
    };
    void tick();

    return () => {
      live = false;
      if (timer !== undefined) clearTimeout(timer);
    };
    // `checkTick` is a dependency so pressing "check now" restarts the loop from
    // attempt 0 with an immediate poll — the add-in's substitute for the web's
    // `visibilitychange` reset, which a task-pane cannot observe on a separate
    // browser window.
  }, [ref, settle, checkTick]);

  return { phase, begin, retryDraft, checkNow, reset };
}

/** Which phase a fresh handoff lands in. A url a browser cannot open → `simulated`. */
function phaseFor(handoff: CheckoutHandoff): CheckoutPhase {
  if (!isOpenable(handoff.checkout_url)) return { kind: "simulated", handoff };
  openExternal(handoff.checkout_url);
  return { kind: "waiting", handoff };
}

/** What the `payment_pending` 409 says: which checkout, and what it is for. */
function pendingCheckout(
  error: unknown,
): { readonly ref: string; readonly url: string; readonly refs: readonly string[] } | null {
  const err = error as { status?: number; detail?: unknown } | null;
  if (err === null || err.status !== 409) return null;
  const detail = err.detail;
  if (typeof detail !== "object" || detail === null) return null;
  const inner = detail as Record<string, unknown>;
  if (inner.error !== "payment_pending") return null;
  const url = inner.checkout_url;
  const refValue = inner.intent_ref;
  if (typeof url !== "string" || url.length === 0) return null;
  if (typeof refValue !== "string" || refValue.length === 0) return null;
  const refs = inner.refs;
  return {
    ref: refValue,
    url,
    refs: Array.isArray(refs) ? refs.filter((r): r is string => typeof r === "string") : [],
  };
}
